<template>
  <form v-if="isVisible">
    <vue-form-generator
      :schema="schema"
      :model="model"
      :options="formOptions"
      @model-updated="modelUpdated"
      @field-updated="submitField"
      @collection-updated="submitCollection"
    />
  </form>
</template>

<script>
import _ from 'lodash'
import api from '../../../../../api'
import ErrorNotify from '../../../../share/mixins/ErrorNotify'
import fieldCollection from '../../form/type/fieldCollection'
import fieldMoney from '../../form/type/fieldMoney'
import FieldRequirementValidator from '../../mixins/FieldRequirementValidator'
import Loader from '../../../../share/Loader'
import Vue from 'vue'
import VueFormGenerator from 'vue-form-generator'

Vue.component('fieldCollection', fieldCollection)
Vue.component('fieldMoney', fieldMoney)
Vue.use(VueFormGenerator)

export default {
  components: {
    VueFormGenerator: VueFormGenerator.component
  },
  mixins: [
    Loader,
    ErrorNotify,
    FieldRequirementValidator
  ],
  props: {
    taskData: { type: Object, required: true, default () { return {} } },
    schemaName: { type: String, required: true }
  },
  data () {
    return {
      events: {
        collapseGroup: 'schema:collapse-group'
      },
      fieldSendDataMap: {
        // collection fields
        deductions: {
          method: this.prepareDeductionsData
        },
        costAdditionals: {
          method: this.prepareCostAdditionalsData
        },
        invoices: {
          method: this.prepareInvoicesData
        }
      },
      isVisible: false,
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: false,
        fieldIdPrefix: 'paymentTask-'
      },
      model: {},
      modelChanged: {},
      schema: {
        groups: []
      },
      schemaCollection: {},
      service: this.$route.meta.acl.service
    }
  },
  mounted () {
    this.loadSchema()
    this.initFieldsListeners()
  },
  updated () {
    this.validateRequirement()
  },
  methods: {
    initFieldsListeners () {
      this.$events.on(this.events.collapseGroup, this.collapseGroup)
    },
    loadData () {
      this.model = Object.assign({}, this.model, this.taskData)
    },
    getLoadSchemaMethod () {
      return this.$isWithClients(this.service)
        ? api.request(this.service, 'get', `/schemas/${this.schemaName}`, { client: this.$route.meta.client })
        : api.request(this.service, 'get', `/schemas/${this.schemaName}`)
    },
    loadSchema () {
      this.getLoadSchemaMethod()
        .then((result) => {
          this.loadData()
          this.buildSchema(result.data)
          this.isVisible = true
          this.$notify({
            type: 'success',
            text: 'Schemat formularza załadowany poprawnie'
          })
        })
        .then(() => {
          this.$events.$emit('dashboard:submenu:taskLoaded')
        })
        .catch(() => {
          this.toggleLoading()
          this.$notify({
            type: 'error',
            text: 'Błąd ładowania schematu formularza'
          })
        })
    },
    buildSchema (schema) {
      for (let g = 0; g < schema.groups.length; g++) {
        const group = schema.groups[g]
        let fields = []
        let groupFields = Object.values(group.fields)
        for (let f = 0; f < groupFields.length; f++) {
          let baseField = groupFields[f]

          if (baseField.field.settings && baseField.field.settings.collection) {
            let collectionName = baseField.field.settings.collection

            if (this.schemaCollection[collectionName] === undefined) {
              this.schemaCollection[collectionName] = []
            }

            this.schemaCollection[collectionName].push(baseField)

            let collection = { 'collection': collectionName }
            if (_.find(fields, collection) === undefined) {
              fields.push(collection)
            }
          } else {
            fields.push(this.createField(baseField))
          }
        }
        if (group.fields.length > 0) {
          this.schema.groups.push(this.createGroup(group, fields))
        }
      }

      this.addFieldCollectionToSchema()
    },
    addFieldCollectionToSchema () {
      for (let g = 0; g < this.schema.groups.length; g++) {
        let group = this.schema.groups[g]
        let groupFields = Object.values(group.fields)
        for (let f = 0; f < groupFields.length; f++) {
          let baseField = groupFields[f]

          if (baseField.collection !== undefined) {
            let baseCollectionFields = []
            let collectionFields = this.schemaCollection[baseField.collection]
            let fieldModel = this.model[baseField.collection]
            let collectionLength = this.getCollectionLength(fieldModel)

            this.$set(this.schema.groups[g].fields[f], 'type', 'collection')
            this.$set(this.schema.groups[g].fields[f], 'model', 'model')
            this.$set(this.schema.groups[g].fields[f], 'schema', { groups: [] })

            for (let c = 0; c < collectionLength; c++) {
              let fields = []

              for (let f = 0; f < collectionFields.length; f++) {
                let createdField = this.createField(collectionFields[f], c)
                createdField.collectionName = baseField.collection
                fields.push(createdField)

                if (c === 0) {
                  let collectionField = _.cloneDeep(createdField)
                  collectionField.model = collectionFields[f].field.model
                  baseCollectionFields.push(collectionField)
                }
              }

              this.schema.groups[g].fields[f]['schema']['groups'].push({ fields: fields })
            }

            this.$set(this.schema.groups[g].fields[f], 'baseCollectionFields', baseCollectionFields)
            this.$set(this.schema.groups[g], 'fieldCollection', true)
          }
        }
      }
    },
    createGroup (group, fields) {
      let result = {
        fields
      }
      if (group.label) {
        result.legend = group.label
      }
      return result
    },
    createField (fieldItem, collectionIndex = null) {
      let field = fieldItem.field

      let fieldParams = {
        type: field.type,
        label: field.label,
        inputName: field.model,
        disabled: this.isFieldDisabled(fieldItem),
        required: fieldItem.required ? fieldItem.required : false,
        relatedFields: []
      }

      let fieldModel = this.getPrepareFieldModel(field, collectionIndex)
      Object.assign(fieldParams, fieldModel)

      if (field.settings !== null) {
        Object.assign(fieldParams, this.setSettings(field))
      }

      return fieldParams
    },
    setSettings (field) {
      let settings = field.settings

      if (settings.value) {
        this.model[field.model] = settings.value
      }

      let fieldParams = {}
      fieldParams.readonly = settings.readonly ? settings.readonly : false
      fieldParams.relatedFields = settings.relatedFields ? settings.relatedFields : []
      Object.assign(fieldParams, this.setSpecificSettings(field))

      return fieldParams
    },
    setSpecificSettings (field) {
      let fieldParams = {}

      switch (field.type) {
        case 'input':
          fieldParams.inputType = field.inputType
          break

        case 'select':
          fieldParams.values = field.settings.values
          fieldParams.selectOptions = { hideNoneSelectedText: true }
          break

        case 'radios':
          fieldParams.values = field.settings.values
          break

        case 'multiselect':
        case 'personMultiselect':
          fieldParams = this.prepareMultiselectParams(field)
          break

        case 'dateTimePicker':
          fieldParams.dateTimePickerOptions = field.settings.dateTimePickerOptions
      }

      return fieldParams
    },
    isFieldDisabled (fieldItem) {
      if (_.has(fieldItem, 'field.settings.readonly')) {
        return fieldItem.field.settings.readonly
      }
      return fieldItem.readonly ? fieldItem.readonly : false
    },
    getCollectionLength (fieldModel) {
      let collectionLength = 1

      if (_.isArray(fieldModel) && fieldModel.length > 1) {
        collectionLength = fieldModel.length
      }

      return collectionLength
    },
    resetCollectionField (collectionPath) {
      if (_.has(this.model, collectionPath)) {
        _.set(this.model, collectionPath, null)
      }
    },
    prepareMultiselectParams (field) {
      let fieldParams = {}
      let fieldModel = this.model[field.model]

      if (field.settings.params) {
        fieldParams.params = field.settings.params
      }

      if (field.settings.source) {
        fieldParams.source = field.settings.source
      }

      if (field.settings.options) {
        fieldParams.options = field.settings.options
      }

      if (_.isArray(fieldModel) && !_.isEmpty(fieldModel)) {
        let collectionField = fieldModel[field.collectionIndex]

        let options = {
          id: collectionField.id,
          label: collectionField.forename + ' ' + collectionField.surname,
          entity: collectionField
        }

        fieldParams.options = [options]
        fieldParams.value = options
      }

      return fieldParams
    },
    getPrepareFieldModel (field, collectionIndex = null) {
      let fieldParams = {}
      let fieldModel = field.model

      if (collectionIndex !== null) {
        fieldParams.collectionIndex = collectionIndex
        fieldParams.model = fieldModel.replace(/\[i\]/g, '[' + collectionIndex + ']')
        fieldParams.modelPlaceholder = field.model
      } else {
        fieldParams.model = field.model
      }

      return fieldParams
    },
    formatSourceModelName (source) {
      return source.split('').filter(char => char !== '[' && char !== ']' && char !== '.').join('')
    },
    modelUpdated (e, source) {
      let model = this.modelChanged
      if (typeof source === 'object') {
        model[this.formatSourceModelName(source.collectionName + 'Collection')] = true
      } else {
        model[this.formatSourceModelName(source)] = true
      }
      this.modelChanged = model
      this.validateRequirement()
    },
    getSendMethod (newVal, schema) {
      return this.$isWithClients(this.service)
        ? api.request(this.service, 'put', `/payments/${this.$route.params.paymentTaskId}`, this.createFieldDataToSend(newVal, schema))
        : api.request(this.service, 'put', `/tasks/${this.$route.params.id}/main/payment/${this.$route.params.paymentTaskId}`, this.createFieldDataToSend(newVal, schema))
    },
    submitField (newVal, schema, target) {
      if (!this.modelChanged[this.formatSourceModelName(schema.model)]) {
        return
      }

      target.classList.add('saving-field')

      // wait for all watchers to finish
      // schemaName should be workflowName
      this.$nextTick(() => {
        this.getSendMethod(newVal, schema)
          .then(() => {
            this.modelChanged[this.formatSourceModelName(schema.model)] = false
            target.classList.remove('saving-field')
            target.classList.add('field-saved')
            setTimeout(() => target.classList.remove('field-saved'), 1500)
          })
          .catch(() => {
            this.modelChanged[this.formatSourceModelName(schema.model)] = false
            target.classList.remove('saving-field')
            target.classList.add('field-error')
            setTimeout(() => target.classList.remove('field-error'), 3600)
            this.$notify({
              type: 'error',
              text: 'Błąd zapisu danych'
            })
          })
      })
    },
    createFieldDataToSend (newVal, schema) {
      let dataToSend = {}

      if (this.fieldSendDataMap[schema.model] !== undefined) {
        newVal = this.fieldSendDataMap[schema.model].method(newVal, ...this.fieldSendDataMap[schema.model].args)
      }
      dataToSend[schema.model] = newVal

      schema.relatedFields.forEach((relatedField) => {
        if (this.fieldSendDataMap[relatedField] !== undefined) {
          dataToSend[relatedField] = this.fieldSendDataMap[relatedField].method(this.model[relatedField], ...this.fieldSendDataMap[relatedField].args)
        }
      })

      return dataToSend
    },
    getSendCollectionMethod (collectionNewVal, schema, shouldRemove) {
      return this.$isWithClients(this.service)
        ? api.request(this.service, 'put', `/payments/${this.$route.params.paymentTaskId}`, this.createCollectionDataToSend(collectionNewVal, schema, shouldRemove))
        : api.request(this.service, 'put', `/tasks/${this.$route.params.id}/main/payment/${this.$route.params.paymentTaskId}?type=collection`, this.createCollectionDataToSend(collectionNewVal, schema, shouldRemove))
    },
    submitCollection (collectionNewVal, schema, target, shouldRemove) {
      if (!this.modelChanged[this.formatSourceModelName(schema.hasOwnProperty('model') ? schema.model : schema.collectionName + 'Collection')]) {
        return
      }

      if (!shouldRemove) {
        target.classList.add('saving-field')
      }

      // wait for all watchers to finish
      this.$nextTick(() => {
        this.getSendCollectionMethod(collectionNewVal, schema, shouldRemove)
          .then((response) => {
            this.modelChanged[this.formatSourceModelName(schema.hasOwnProperty('model') ? schema.model : schema.collectionName + 'Collection')] = false

            // update given collection item id
            if (response.data.processedEntityId) {
              this.model[schema.collectionName][schema.collectionIndex].id = response.data.processedEntityId
            }

            if (shouldRemove) {
              this.$notify({
                type: 'success',
                text: 'Usunięto wpis'
              })
            } else {
              target.classList.remove('saving-field')
              target.classList.add('field-saved')
              setTimeout(() => target.classList.remove('field-saved'), 1500)
            }
          })
          .catch(() => {
            this.modelChanged[this.formatSourceModelName(schema.hasOwnProperty('model') ? schema.model : schema.collectionName + 'Collection')] = false
            if (!shouldRemove) {
              target.classList.remove('saving-field')
              target.classList.add('field-error')
              setTimeout(() => target.classList.remove('field-error'), 3600)
            }

            this.$notify({
              type: 'error',
              text: 'Błąd zapisu danych'
            })
          })
      })
    },
    createCollectionDataToSend (collectionNewVal, schema, shouldRemove) {
      let dataToSend = {}
      if (this.$isWithClients(this.service)) {
        dataToSend.type = 'collection'
      }

      dataToSend.collectionName = schema.collectionName

      if (this.fieldSendDataMap[schema.collectionName] !== undefined) {
        collectionNewVal = this.fieldSendDataMap[schema.collectionName].method(collectionNewVal)
      }
      dataToSend[schema.collectionName] = collectionNewVal

      if (shouldRemove) {
        dataToSend.propertiesChanged = Object.keys(collectionNewVal).filter(prop => {
          const propsToBeOmitted = [
            'id',
            'taskPayment'
          ]
          return !propsToBeOmitted.includes(prop)
        }).map(prop => schema.collectionName + '[i].' + prop)

        dataToSend.action = 'delete'
      } else {
        // convert to default collection property name to match form field model name
        dataToSend.propertiesChanged = [schema.model.replace(/\[\d+\]/g, '[i]')]
      }

      return dataToSend
    },
    prepareDeductionsData (collectionNewVal) {
      return this.prepareCollectionData(collectionNewVal)
    },
    prepareCostAdditionalsData (collectionNewVal) {
      return this.prepareCollectionData(collectionNewVal)
    },
    prepareInvoicesData (collectionNewVal) {
      return this.prepareCollectionData(collectionNewVal)
    },
    prepareCollectionData (collectionNewVal) {
      return {
        id: this.getModelDataByPath(collectionNewVal, 'id'),
        taskPayment: this.$route.params.paymentTaskId,
        value: this.getModelDataByPath(collectionNewVal, 'value'),
        type: this.getModelDataByPath(collectionNewVal, 'type'),
        description: this.getModelDataByPath(collectionNewVal, 'description')
      }
    },
    getModelDataByPath (model, prop) {
      if (typeof model === 'string') {
        return model
      }

      return _.has(model, prop) ? _.get(model, prop) : null
    },
    collapseGroup (event) {
      // TODO for future collapsing groups
      // let groupItems = document.querySelectorAll(`#${event.currentTarget.id} ~ div`)
      // groupItems.forEach((el, index) => {
      //   if (!el.classList.contains('collapse')) {
      //     // el.classList.add('collapse')
      //   } else {
      //     // el.classList.remove('collapse')
      //   }
      // })
    }
  }
}
</script>
